import React from 'react';
import type { SearchSuggestion } from '../view-models/search-suggestion';
import { highlightMatch } from '../utils/suggestion-highlight-match';
import { cn } from '../../../utils/cn';
import { useAccessibleClick } from '../../../hooks/use-accessible-click';

/**
 * Extracts the last segment from a category link for subtitle display.
 */
const getCategoryLeaf = (categoryLink: string) => {
    const segments = categoryLink.split('/').filter(Boolean);
    const leaf = segments.at(-1)?.replace(/-/g, ' ').trim();

    if (!leaf) {
        return '';
    }

    return leaf
        .toLowerCase()
        .replace(/\b\w/g, (character) => character.toUpperCase());
};

/**
 * Individual item in mixed suggestions display
 * Displays a search suggestion with an icon/logo and text content
 */
export const MixedSuggestionItem: React.FC<{
    option: SearchSuggestion | string;
    groupType: 'keyword' | 'categories' | 'professionals';
    query: string;
    variant: 'default' | 'header';
    menuColor: 'light' | 'dark';
    onClick: () => void;
}> = ({ option, groupType, query, variant, menuColor, onClick }) => {
    const { onClick: handleClick, onKeyDown } = useAccessibleClick(onClick);
    const isKeyword = typeof option === 'string';
    const suggestion = isKeyword ? null : option;

    const isProfessional = groupType === 'professionals';
    const isCategory = groupType === 'categories';
    const showProfessionalLogo = isProfessional && suggestion?.image;
    const professionalCategory =
        !isKeyword && isProfessional && suggestion?.categoryLink
            ? getCategoryLeaf(suggestion.categoryLink)
            : '';
    const subtitle = !isKeyword
        ? isProfessional
            ? professionalCategory
            : suggestion?.type
        : '';

    return (
        <div
            onClick={handleClick}
            onKeyDown={onKeyDown}
            className={cn(
                'group flex cursor-pointer items-center gap-2 focus:outline-none md:p-2',
                variant === 'header' && menuColor === 'light'
                    ? 'md:p-0 md:focus:bg-white/10'
                    : variant === 'header' && menuColor === 'dark'
                      ? 'md:p-0 md:focus:bg-gray-200'
                      : 'md:hover:bg-gray-100 md:focus:bg-gray-100'
            )}
            role="option"
            tabIndex={0}
            aria-selected={false}
        >
            {/* Logo/Icon */}
            {showProfessionalLogo ? (
                <div
                    className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full p-0.5"
                    style={{
                        backgroundColor: suggestion.logoBackground || 'white',
                    }}
                >
                    <img
                        src={suggestion.image}
                        alt={suggestion.title}
                        className="h-full w-full object-contain"
                    />
                </div>
            ) : (
                <div
                    className={cn(
                        'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full',
                        variant === 'header' && menuColor === 'light'
                            ? 'bg-white/10'
                            : 'bg-gray-200'
                    )}
                >
                    {isCategory ? (
                        <svg
                            className={cn(
                                'h-5 w-5',
                                variant === 'header' && menuColor === 'light'
                                    ? 'text-white/60'
                                    : 'text-gray-500'
                            )}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                        </svg>
                    ) : (
                        <svg
                            className={cn(
                                'h-5 w-5',
                                variant === 'header' && menuColor === 'light'
                                    ? 'text-white/60'
                                    : 'text-gray-500'
                            )}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                    )}
                </div>
            )}

            {/* Text content */}
            <div className="flex min-w-0 flex-1 flex-col">
                <div className="truncate text-sm font-medium md:text-base">
                    <span
                        className={cn(
                            'inline leading-[1.1] group-hover:underline',
                            variant === 'header' && menuColor === 'light'
                                ? 'text-white'
                                : variant === 'header' && menuColor === 'dark'
                                  ? 'text-gray-800'
                                  : 'text-gray-900'
                        )}
                    >
                        {isKeyword
                            ? highlightMatch(option, query, variant, menuColor)
                            : suggestion
                              ? highlightMatch(
                                    suggestion.title,
                                    query,
                                    variant,
                                    menuColor
                                )
                              : ''}
                    </span>
                </div>
                {!isKeyword && subtitle && (
                    <span
                        className={cn(
                            'truncate text-xs text-gray-600 md:text-sm',
                            variant === 'header' && menuColor === 'light'
                                ? 'text-white/60'
                                : variant === 'header' && menuColor === 'dark'
                                  ? 'text-gray-600'
                                  : ''
                        )}
                    >
                        {subtitle}
                    </span>
                )}
            </div>
        </div>
    );
};
